import { Component, ErrorInfo, ReactNode } from 'react';
import { toErrorMessage } from '../utils/errorMessage';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  // React passes whatever was thrown — not necessarily an Error instance.
  error: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#eef2ff] px-4">
          <div className="glass-panel max-w-lg text-center">
            <span className="ghost-chip">Unexpected</span>
            <h1 className="mt-4 text-3xl font-semibold text-slate-900">
              Something went wrong
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {toErrorMessage(this.state.error, 'An unexpected error occurred')}
            </p>
            <button
              type="button"
              className="mt-6 w-full rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:opacity-90"
              onClick={this.handleReset}
            >
              Go to Overview
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
